/**
 * Daily Report — port of the original views.report. The report itself comes from services.reports
 * (useDailyReport); the plain text is exactly buildReport() of that data, which is what "Copy report" copies.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { fmtDate, fmtH, sum, today } from '@/domain/format';
import { buildReport, reportDates } from '@/domain/report';
import { useDailyReport, useDataset } from '@/hooks/data';
import { useToast } from '@/app/AppContext';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { ErrorState, LoadingState, StatCard, Topbar } from '@/ui/kit';
import { useAppActions } from '../actions';
import { useDocxImport } from '../imports/useDocxImport';
import { ReportFormatted, ReportPlainText } from './ReportViews';

/** UI preference only: which tab (Formatted / Plain text) the viewer last used. */
const VIEW_KEY = 'easysaas.reportView';
type ReportView = 'f' | 't';
const readView = (): ReportView => {
  try {
    return localStorage.getItem(VIEW_KEY) === 't' ? 't' : 'f';
  } catch {
    return 'f';
  }
};

export function DailyReportPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const act = useAppActions();
  const importDocx = useDocxImport();
  const ds = useDataset();
  const dates = useMemo(() => (ds.data ? reportDates(ds.data) : []), [ds.data]);
  // ?d=, else the latest day with data, else today (decided once the data is loaded)
  const date = params.get('d') || (ds.data ? dates[0] || today() : '');
  const rep = useDailyReport(date);
  const text = useMemo(() => (rep.data ? buildReport(rep.data) : ''), [rep.data]);
  const [view, setView] = useState<ReportView>(readView);
  const show = (v: ReportView) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* preference only */
    }
  };
  const copy = () =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(text))
      .then(
        () => toast('Report copied to clipboard.'),
        () => toast('Could not copy. Select the text manually.', true),
      );
  const header = (
    <Topbar title="Daily Report" sub="Generated from the work log and daily updates — copy and send">
      <input type="date" value={date} style={{ width: 'auto' }} aria-label="Report date" onChange={(e) => nav(`/daily-report?d=${e.target.value}`)} />
      <PermissionGate perm="backup:manage">
        <label className="btn" style={{ margin: 0, color: 'var(--text)', fontSize: 14 }} data-tip="Upload the team's Daily Update .docx">
          Import .docx
          <input
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void importDocx(f);
            }}
          />
        </label>
      </PermissionGate>
      <PermissionGate perm="updates:write">
        <button className="btn" onClick={() => act.projectLog()}>
          + Project Log Work
        </button>
      </PermissionGate>
      <PermissionGate perm="worklog:write">
        <button className="btn" onClick={() => act.workLog()}>
          + Employee Log Work
        </button>
      </PermissionGate>
      <button className="btn" onClick={() => window.print()}>
        Print / PDF
      </button>
      <button className="btn primary" onClick={() => void copy()} disabled={!rep.data}>
        Copy report
      </button>
    </Topbar>
  );
  const error = ds.error || rep.error;
  if (error)
    return (
      <>
        {header}
        <ErrorState
          error={error}
          onRetry={() => {
            ds.refetch();
            void rep.refetch();
          }}
        />
      </>
    );
  if (!ds.data || !rep.data) return (<>{header}<LoadingState /></>);
  const logs = ds.data.logs.filter((l) => l.date === date);
  const updates = ds.data.updates.filter((u) => u.date === date);
  const projectsTouched = new Set([...logs.map((l) => l.projectId), ...updates.map((u) => u.projectId)].filter(Boolean)).size;
  return (
    <>
      {header}
      <div className="cards no-print">
        <StatCard n={fmtH(sum(logs.map((l) => l.hours)))} label={`Hours on ${fmtDate(date)}`} />
        <StatCard n={new Set(logs.map((l) => l.memberId)).size} label="People worked" />
        <StatCard n={projectsTouched} label="Projects touched" />
        <StatCard to={`/work-log?d=${date}`} n={logs.length} label="Log entries →" />
      </div>
      <div className="panel">
        <div className="panel-h no-print">
          <h2>Report for {fmtDate(date)}</h2>
          <div className="row">
            <span className="hint">Other days:</span>
            {dates.slice(0, 7).map((d) => (
              <Link key={d} to={`/daily-report?d=${d}`} className={'btn sm' + (d === date ? ' primary' : '')}>
                {fmtDate(d)}
              </Link>
            ))}
          </div>
        </div>
        <div className="panel-b">
          <div className="tabs no-print" role="tablist">
            <button type="button" role="tab" aria-selected={view === 'f'} className={'tab' + (view === 'f' ? ' active' : '')} onClick={() => show('f')}>
              Formatted
            </button>
            <button type="button" role="tab" aria-selected={view === 't'} className={'tab' + (view === 't' ? ' active' : '')} onClick={() => show('t')}>
              Plain text
            </button>
            <span className="hint" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
              Copy report copies the plain text.
            </span>
          </div>
          <div id="reportFmt" hidden={view !== 'f'}>
            <ReportFormatted report={rep.data} />
          </div>
          <div className="report" id="reportText" hidden={view === 'f'}>
            <ReportPlainText text={text} projectNames={ds.data.projects.map((p) => p.name)} />
          </div>
          <div className="hint no-print" style={{ marginTop: 10 }}>
            Each project shows Employee Log Work first, then its Project Log Work (update + statistics). Either part is left out when empty. Edit the title in Settings.
          </div>
        </div>
      </div>
    </>
  );
}
